import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { toast } from '@/components/feedback/useToast';
import { useResetPassword } from '@/features/admins/hooks/use-admin-mutations';
import { getAdminErrorMessage } from '@/features/admins/utils/error-message';
import { EmailPreview } from '@/components/email/EmailPreview';

// Two entry modes (PRD 15 §3.6 + customer decisions log 2026-05-22):
//   ?send=true&id=&name=&email=  — send mode (edit-panel button): action bar with
//                                  Cancel + Send shown above the email card.
//   (no ?send=true)              — preview-only mode: just back link above the email.
//
// The email body is the BE single-owner render (GET /admin/email-previews/admin-password-reset,
// from messages_ko) — the FE no longer templates it. The Send button calls
// POST /admin/admins/{id}/password-reset (mock until VITE_USE_MOCK_API=false). No confirmation
// modal: clicking [Reset Password] in the edit panel navigates here directly.

export function AdminPasswordResetEmailPage() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetMutation = useResetPassword();

  const sendMode = searchParams.get('send') === 'true';
  const idParam = searchParams.get('id');
  const id = idParam ? Number(idParam) : NaN;
  const nameParam = searchParams.get('name');
  const emailParam = searchParams.get('email');
  const name = nameParam || t('adminManagement.passwordResetEmail.fallbackName');

  // SHCT-651 — these feed the server render's ONE meta box; this page no longer paints its own
  // copy on top of it (that is what showed the header twice). Values are populated
  // from the selected record (the BE-rendered body stays generic).
  //
  // SHCT-684 — `recipientEmail` carries the BARE address, never `name <email>`. The 받는사람 row is
  // composed by the message itself from name and address as two separate arguments, so a
  // pre-composed value was wrapped a second time and rendered `name <name <email>>`. Every live
  // send path passes the bare address, and the preview must match it. With no ?email= the slot is
  // left empty rather than repeating the name, which would read as an address.
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const sentAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const recipient = nameParam
    ? emailParam ?? ''
    : t('adminManagement.passwordResetEmail.metaRecipientFallback');

  const returnToList = () => navigate('/admin-management');

  const handleSend = async () => {
    if (!Number.isFinite(id) || resetMutation.isPending) return;
    try {
      await resetMutation.mutateAsync(id);
      toast.success(t('adminManagement.toasts.resetSuccess'));
      returnToList();
    } catch (e) {
      // SHCT-685 — this branch used to print `e.message` for any AdminApiError, which put the
      // server's own text on screen instead of PRD 15 §8.2's copy. The shared resolver decides per
      // code whether server text may be shown at all: system codes fall back to the operation copy
      // below, and only business codes the FE models may speak for themselves.
      toast.error(
        getAdminErrorMessage(e, t, 'adminManagement.toasts.resetError'),
      );
    }
  };

  return (
    <div className="admin-email-preview-page">
      {!sendMode && (
        <div className="admin-email-preview-page__back">
          <Link to="/admin-management">
            {t('adminManagement.passwordResetEmail.backToList')}
          </Link>
        </div>
      )}

      {sendMode && (
        // Card-style send action bar (prototype .pwreset-send-bar), sized to match the email card.
        <div className="admin-pwreset-send-bar">
          <div
            className="admin-pwreset-send-bar__info"
            dangerouslySetInnerHTML={{
              __html: t('adminManagement.passwordResetEmail.actionBarInfo', {
                name,
              }),
            }}
          />
          <div className="admin-pwreset-send-bar__actions">
            <button
              type="button"
              onClick={returnToList}
              disabled={resetMutation.isPending}
              className="admin-pwreset-send-bar__cancel"
            >
              {t('adminManagement.passwordResetEmail.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={resetMutation.isPending || !Number.isFinite(id)}
              className="admin-pwreset-send-bar__send"
            >
              {resetMutation.isPending
                ? t('adminManagement.passwordResetEmail.sending')
                : t('adminManagement.passwordResetEmail.send')}
            </button>
          </div>
        </div>
      )}

      <EmailPreview
        kind="admin-password-reset"
        vars={{ name, recipientEmail: recipient, sentAt }}
      />
    </div>
  );
}
